import type { GenerationSettings } from "./generation-settings.ts";

export const DEFAULT_GENERATION_CONFIG_ID = "00000000-0000-4000-8000-000000000001";
export const DEFAULT_GENERATION_CONFIG_NAME = "Default";
export const GENERATION_CONFIG_NAME_MAX_LENGTH = 80;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface GenerationConfig {
  id: string;
  name: string;
  revision: number;
  settings: GenerationSettings;
  createdByUserId: string | null;
  createdByEmail: string | null;
  changedByUserId: string | null;
  changedByEmail: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GenerationConfigRegistry {
  defaultConfigId: string;
  revision: number;
  changedByUserId: string | null;
  changedByEmail: string | null;
  updatedAt: Date;
}

export interface GenerationConfigSelection {
  id: string;
  revision: number;
}

export function normalizeGenerationConfigName(value: unknown): string {
  if (typeof value !== "string") throw new Error("generation configuration name must be text");
  const name = value.trim();
  if (!name) throw new Error("generation configuration name must not be blank");
  if (name.length > GENERATION_CONFIG_NAME_MAX_LENGTH) {
    throw new Error(
      `generation configuration name must be at most ${GENERATION_CONFIG_NAME_MAX_LENGTH} characters`,
    );
  }
  return name;
}

export function normalizeGenerationConfigRevision(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 1) {
    throw new Error("generation configuration revision must be a positive safe integer");
  }
  return value;
}

export function normalizeGenerationConfigId(value: unknown): string {
  if (typeof value !== "string" || !UUID_PATTERN.test(value)) {
    throw new Error("generation configuration ID must be a UUID");
  }
  return value.toLowerCase();
}

export function normalizeGenerationConfigSelection(
  id: unknown,
  revision: unknown,
): GenerationConfigSelection | null {
  if (id === undefined && revision === undefined) return null;
  if (id === undefined || revision === undefined) {
    throw new Error("generation configuration ID and revision must be supplied together");
  }
  return {
    id: normalizeGenerationConfigId(id),
    revision: normalizeGenerationConfigRevision(revision),
  };
}
